import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'wouter';

import LoadingSvg from '@/components/common/LoadingSvg';
import { useUpcomingPayments, EasyPayScheduleItem } from '@/components/myaccount/hooks/useEasyPaySchedule';
import { useAuthenticated } from '@/components/shopping_bag/hooks/useAuthenticated';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progressbar';

import { CardExpiryWarning } from './CardExpiryWarning';
import { EasyPayDetailDialog } from './EasyPayDetailDialog';

function parseNoOfEasyPay(noOfEasyPay: string): [number, number] {
    // Example input: "3 of 12", "final of 6"
    const parts = noOfEasyPay.trim().split(' of ');
    if (parts.length !== 2) return [0, 0];

    const current = Number(parts[0]); // ✅ Always a number now
    const total = Number(parts[1]);

    if (isNaN(current) || isNaN(total)) return [0, 0];

    return [current, total];
}

const EasyPayUpcomingPayments: React.FC = () => {
    const [selectedOrder, setSelectedOrder] = useState<EasyPayScheduleItem | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(true);
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [location] = useLocation();

    const isAuthenticated = useAuthenticated();
    const { data, isFetching, error } = useUpcomingPayments(isAuthenticated, page, pageSize);

    // Trigger animation when data changes
    useEffect(() => {
        if (data?.InstallmentOrders) {
            setIsAnimating(true);
            // Small delay to allow DOM to render with 0, then animate
            const timer = setTimeout(() => {
                setIsAnimating(false);
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [data, isFetching, page]);

    const handleProgressClick = (order: EasyPayScheduleItem) => {
        setSelectedOrder(order);
        setIsDialogOpen(true);
    };

    // Generate pagination indices (up to 10 adjacent pages)
    const paginationIndices = useMemo(() => {
        if (!data?.Paging) return [];

        const indices = [];
        const halfSize = 5;

        // Add pages before current
        for (let i = 0; i < halfSize; i++) {
            indices.push(page - i);
        }

        // Add pages after current
        for (let i = 1; i < 2 * halfSize; i++) {
            indices.push(page + i);
        }

        const maxPage = data.Paging.TotalPages <= 10 ? data.Paging.TotalPages : 10;
        const filtered = indices.filter((i) => i > 0 && i <= data.Paging.TotalPages).sort((a, b) => a - b);

        const arr: number[] = [];
        for (let i = 0; i < Math.min(maxPage, filtered.length); i++) {
            arr.push(filtered[i]);
        }

        return arr;
    }, [page, data?.Paging]);

    useEffect(() => {
        // Reset to page 1 when route changes
        setPage(1);
    }, [location]);

    const scrollToTopOfEasyPay = () => {
        const toScrollPosition = document.getElementById('easypay-scroll-position');
        if (toScrollPosition) {
            toScrollPosition.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Error state
    if (error) {
        return (
            <div className="easypay px-5 md:px-0">
                <div className="flex min-h-96 items-center justify-center bg-white">
                    <div className="flex flex-col items-center text-red-600">Error Loading EasyPay</div>
                </div>
            </div>
        );
    }

    // Parent component handles loading/error/empty states, data is guaranteed here
    if (!data?.InstallmentOrders || !data.Paging) {
        return null;
    }

    return (
        <div className="w-full">
            <div className="relative grid grid-cols-[1fr_1.1fr_.8fr] text-xs font-light leading-6 md:text-sm md:leading-6 md2:grid-cols-[1.3fr_1.15fr_.83fr_1.15fr_1.15fr] lg:grid-cols-[.9fr_.9fr_.8fr_1.15fr_1.15fr]">
                {isFetching && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#f8f8f8]/70">
                        <div className="flex flex-col items-center">
                            <LoadingSvg />
                            <span className="mt-2 font-semibold">Loading Easy Pay Schedule</span>
                        </div>
                    </div>
                )}
                {/* Mobile/Tablet Headers */}
                <div
                    style={{ borderBottom: '1px solid #000' }}
                    className="py-2 pr-1 text-xxs font-bold uppercase leading-5 text-black xsm:text-xs xsm:leading-5 md2:hidden"
                >
                    Bill Date <br /> Order Number
                </div>
                <div
                    style={{ borderBottom: '1px solid #000' }}
                    className="py-2 pr-1 text-xxs font-bold uppercase leading-5 text-black xsm:text-xs xsm:leading-5 md2:hidden"
                >
                    Amount Due <br /> Payment Progress
                </div>
                <div
                    style={{ borderBottom: '1px solid #000' }}
                    className="py-2 text-xxs font-bold uppercase leading-5 text-black xsm:text-xs xsm:leading-5 md2:hidden"
                >
                    Pay Method
                </div>
                {/* Desktop Headers */}
                <div
                    className="hidden py-2 font-bold uppercase text-black md:text-xs md2:!block"
                    style={{ borderBottom: '1px solid #000' }}
                >
                    Bill Date
                </div>
                <div
                    className="hidden py-2 font-bold uppercase text-black md:text-xs md2:!block"
                    style={{ borderBottom: '1px solid #000' }}
                >
                    Order Number
                </div>
                <div
                    className="hidden py-2 pr-1 font-bold uppercase text-black md:text-xs md2:!block"
                    style={{ borderBottom: '1px solid #000' }}
                >
                    Amount Due
                </div>
                <div
                    className="hidden py-2 font-bold uppercase text-black md:text-xs md2:!block"
                    style={{ borderBottom: '1px solid #000' }}
                >
                    Payment Progress
                </div>
                <div
                    className="hidden py-2 font-bold uppercase text-black md:text-xs md2:!block"
                    style={{ borderBottom: '1px solid #000' }}
                >
                    Pay Method
                </div>

                {/* Data Rows */}
                {data.InstallmentOrders.map((row, idx) => {
                    const [current, total] = parseNoOfEasyPay(row.NoOfEasyPay);
                    const progressValue = total ? (current / total) * 100 : 0;

                    return (
                        <React.Fragment key={row.FullOrderNo}>
                            {/* Mobile/Tablet: 3 columns */}
                            <div className="pt-4 md2:hidden">
                                <div className="flex gap-1 font-semibold">
                                    {row.NextBillDate}
                                    <CardExpiryWarning expiresInDays={row.CardExpiresInDays} hideWhenDetached={true} />
                                </div>
                                <div className="underline">
                                    <a
                                        href={`/pages/myaccount/orderstatus?orderNo=${row.FullOrderNo}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {row.FullOrderNo}
                                    </a>
                                </div>
                            </div>
                            <div className="pt-4 md2:hidden">
                                <div className="">{row.InstallmentAmount}</div>
                                <div className="flex items-center gap-2">
                                    <div className="w-1/2">
                                        <Progress
                                            key={`mobile-${row.FullOrderNo}-${idx}`}
                                            value={isAnimating ? 0 : progressValue}
                                        />
                                    </div>
                                    <div className="flex w-1/2 cursor-pointer items-center underline">
                                        <button type="button" onClick={() => handleProgressClick(row)}>
                                            {row.NoOfEasyPay}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col pt-4 md2:hidden">
                                <span>{row.CardType}</span> <span>{row.MaskedToken}</span>{' '}
                                <span>Expires: {row.CardExpiry}</span>
                            </div>

                            {/* Desktop: 5 columns, spread out */}
                            <div className="hidden gap-1 pt-4 font-semibold md2:!flex">
                                {row.NextBillDate}{' '}
                                <CardExpiryWarning expiresInDays={row.CardExpiresInDays} hideWhenDetached={true} />
                            </div>
                            <div className="hidden pt-4 underline md2:!block">
                                <a
                                    href={`/pages/myaccount/orderstatus?orderNo=${row.FullOrderNo}`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {row.FullOrderNo}
                                </a>
                            </div>
                            <div className="hidden pt-4 md2:!block">{row.InstallmentAmount}</div>
                            <div className="hidden pt-4 md2:!block">
                                <div className="flex items-center gap-2">
                                    <div className="w-1/2">
                                        <Progress
                                            key={`desktop-${row.FullOrderNo}-${idx}`}
                                            value={isAnimating ? 0 : progressValue}
                                        />
                                    </div>
                                    <div className="flex w-1/2 cursor-pointer underline">
                                        <button type="button" onClick={() => handleProgressClick(row)}>
                                            {row.NoOfEasyPay}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="hidden pt-4 md2:!flex md2:flex-row md2:flex-wrap">
                                <div className="mr-4">{row.CardType} </div>
                                <div className="mr-4">{row.MaskedToken}</div>
                                <div className="basis-full">
                                    <span className="whitespace-nowrap">Expires: {row.CardExpiry}</span>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
            {/* Pagination Section */}
            {data.Paging.TotalPages > 1 && (
                <>
                    {/* Horizontal Divider */}
                    <div className="mt-3" style={{ borderBottom: '1px solid #000' }}></div>

                    {/* Display Items Count */}
                    <div className="mr-2 mt-3 flex justify-end text-sm">
                        <span className="mr-1 font-bold">Displaying items:</span>
                        <span className="font-normal">
                            {(data.Paging.CurrentPage - 1) * pageSize + 1} -{' '}
                            {Math.min(data.Paging.CurrentPage * pageSize, data.Paging.TotalRecords)} out of{' '}
                            {data.Paging.TotalRecords}
                        </span>
                    </div>

                    {/* Horizontal Divider */}
                    <div className="mt-3" style={{ borderBottom: '1px solid #000' }}></div>

                    {/* Pagination Controls */}
                    <div className="my-5 flex justify-center">
                        {/* Page Numbers */}
                        {paginationIndices.map((index) => (
                            <button
                                key={index}
                                className={`rounded px-4 py-2 text-sm text-gray-700 transition-all hover:font-bold ${
                                    index === page ? 'font-bold' : 'font-medium'
                                }`}
                                onClick={() => {
                                    setPage(index);
                                    scrollToTopOfEasyPay();
                                }}
                            >
                                {index}
                            </button>
                        ))}
                    </div>
                </>
            )}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent fullscreenOnMobile className="sm:rounded-sm">
                    {selectedOrder && <EasyPayDetailDialog order={selectedOrder} />}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EasyPayUpcomingPayments;
